import type { CompleteMicroSegmentationResult, MicroUnit } from '../types/segmentationTypes'
import {
  buildLedgerSafeWitnessUnitStream,
  type LedgerSafeUnitStorageLocation
} from './ledgerSafeWitnessUnitStream'

export interface OrderedWitnessUnit {
  /** @deprecated Compatibility coordinates for the legacy persistence envelope. */
  chunkIndex: number
  /** @deprecated Compatibility coordinates for the legacy persistence envelope. */
  unitIndex: number
  storage?: LedgerSafeUnitStorageLocation
  unit: MicroUnit
}

export const getOrderedUnitUuid = (unit: MicroUnit): string =>
  String(unit.unit_uuid || unit.unit_id || '')

export const getOrderedWitnessUnits = (
  segmentationResult: CompleteMicroSegmentationResult | null | undefined
): OrderedWitnessUnit[] => {
  return buildLedgerSafeWitnessUnitStream(segmentationResult).map(entry => ({
    unit: entry.unit,
    storage: entry.storage,
    chunkIndex: entry.storage.containerIndex,
    unitIndex: entry.storage.unitIndex
  }))
}

export const getOrderedWitnessUnitStorage = (
  entry: OrderedWitnessUnit
): LedgerSafeUnitStorageLocation => entry.storage || {
  containerIndex: entry.chunkIndex,
  unitIndex: entry.unitIndex
}

export const buildChunkTextSourceCandidate = (
  segmentationResult: CompleteMicroSegmentationResult | null | undefined
): string => {
  const chunks = segmentationResult && Array.isArray(segmentationResult.chunk_results)
    ? segmentationResult.chunk_results
    : []
  const chunksWithText = chunks
    .map((chunk, index) => {
      const chunkText = (chunk as { chunk_text?: unknown })?.chunk_text
      return {
        index,
        text: typeof chunkText === 'string' ? chunkText : '',
        start: Number(chunk?.chunk_position_data?.start_position ?? chunk?.chunk_position_data?.relative_start)
      }
    })
    .filter(chunk => chunk.text.length > 0)

  if (chunksWithText.length === 0) {
    return ''
  }
  if (chunks.length === 1 && chunksWithText.length === 1) {
    return chunksWithText[0].text
  }
  if (chunksWithText.length !== chunks.length || chunksWithText.some(chunk => !Number.isFinite(chunk.start))) {
    return ''
  }

  const sourceLength = chunksWithText.reduce(
    (maxLength, chunk) => Math.max(maxLength, chunk.start + chunk.text.length),
    0
  )
  const chars = new Array<string>(sourceLength).fill(' ')
  chunksWithText.forEach(chunk => {
    for (let offset = 0; offset < chunk.text.length; offset += 1) {
      chars[chunk.start + offset] = chunk.text[offset]
    }
  })
  return chars.join('')
}

export const scoreSourceTextAgainstUnits = (
  sourceText: string,
  orderedUnits: OrderedWitnessUnit[]
): { checked: number; exact: number } => {
  if (!sourceText || orderedUnits.length === 0) {
    return { checked: 0, exact: 0 }
  }

  return orderedUnits.reduce((score, entry) => {
    const unit = entry.unit
    const start = Number(unit.start_position)
    const end = Number(unit.end_position)
    const expectedText = String(unit.unit_text_content || '')
    if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start || !expectedText) {
      return score
    }
    score.checked += 1
    if (sourceText.slice(start, end) === expectedText) {
      score.exact += 1
    }
    return score
  }, { checked: 0, exact: 0 })
}

export const resolveLedgerSafeSegmentationSourceText = ({
  segmentationResult,
  fallbackSourceText
}: {
  segmentationResult: CompleteMicroSegmentationResult | null | undefined
  fallbackSourceText: string
}): string => {
  const orderedUnits = getOrderedWitnessUnits(segmentationResult)
  const chunkTextSource = buildChunkTextSourceCandidate(segmentationResult)
  if (!chunkTextSource) {
    return fallbackSourceText
  }

  const fallbackScore = scoreSourceTextAgainstUnits(fallbackSourceText, orderedUnits)
  const chunkScore = scoreSourceTextAgainstUnits(chunkTextSource, orderedUnits)
  if (
    chunkScore.checked > 0 &&
    chunkScore.exact === chunkScore.checked &&
    chunkScore.exact > fallbackScore.exact
  ) {
    return chunkTextSource
  }
  return fallbackSourceText
}
